import { useEffect, useRef, useState } from "react";
import {
    Button,
    Card,
    Input,
    QRCode,
    Rate,
    Space,
    Tag,
    message,
} from "antd";
import { CopyOutlined, DownloadOutlined, PictureOutlined } from "@ant-design/icons";
import { updateAppointmentCare, CareSurveyRatings } from "../../api/AppointmentCare";

const { TextArea } = Input;

const RATING_FIELDS: { key: keyof CareSurveyRatings; title: string }[] = [
    { key: "quality", title: "Chất lượng dịch vụ" },
    { key: "staff_attitude", title: "Thái độ nhân viên" },
    { key: "doctor_ktv", title: "Bác sĩ / KTV" },
    { key: "facilities", title: "Cơ sở vật chất" },
];

const EMPTY_RATINGS: CareSurveyRatings = { quality: 0, staff_attitude: 0, doctor_ktv: 0, facilities: 0 };

interface Props {
    appointmentId?: number;
    survey?: { care_note?: string; ratings?: Partial<CareSurveyRatings> } | null;
    isCared?: boolean;
    caredBy?: string | null;
    caredAt?: string | null;
    onUpdated?: () => void;
}

export default function CsatTab({ appointmentId, survey, isCared, caredBy, caredAt, onUpdated }: Props) {
    const [ratings, setRatings] = useState<CareSurveyRatings>(EMPTY_RATINGS);
    const [careNote, setCareNote] = useState("");
    const [saving, setSaving] = useState(false);
    // Đã chăm sóc thì mặc định xem phiếu (giao diện gốc), bấm "Chỉnh sửa" mới mở form
    const [editing, setEditing] = useState(false);
    const qrRef = useRef<HTMLDivElement>(null);

    // Link khảo sát công khai gửi cho khách (route /khao-sat/:id, không cần đăng nhập)
    const surveyUrl = appointmentId ? `${window.location.origin}/khao-sat/${appointmentId}` : "";

    const handleCopyLink = async () => {
        if (!surveyUrl) return;
        try {
            await navigator.clipboard.writeText(surveyUrl);
            message.success("Đã sao chép link khảo sát");
        } catch {
            // clipboard API chỉ chạy ở secure context, fallback cho trường hợp còn lại
            const el = document.createElement("textarea");
            el.value = surveyUrl;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            message.success("Đã sao chép link khảo sát");
        }
    };

    /**
     * Dựng ảnh QR để gửi cho khách.
     * Canvas của antd có nền TRONG SUỐT và không chừa lề trắng, gửi thẳng lên khung chat
     * nền tối là không quét được -> vẽ lại lên nền trắng, thêm lề và phóng to cho nét.
     */
    const buildQrCanvas = (): HTMLCanvasElement | null => {
        const source = qrRef.current?.querySelector("canvas");
        if (!source) return null;
        const size = 640;
        const padding = 40;
        const out = document.createElement("canvas");
        out.width = size + padding * 2;
        out.height = size + padding * 2;
        const ctx = out.getContext("2d");
        if (!ctx) return null;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.imageSmoothingEnabled = false; // giữ cạnh ô vuông sắc nét khi phóng to
        ctx.drawImage(source, padding, padding, size, size);
        return out;
    };

    const handleDownloadQR = () => {
        const canvas = buildQrCanvas();
        if (!canvas) {
            message.error("Chưa tạo được ảnh QR, vui lòng thử lại");
            return;
        }
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `khao-sat-${appointmentId}.png`;
        a.click();
    };

    // Chép thẳng ảnh QR vào clipboard để dán luôn vào Zalo/Messenger, khỏi qua bước tải file
    const handleCopyQR = async () => {
        const canvas = buildQrCanvas();
        if (!canvas) {
            message.error("Chưa tạo được ảnh QR, vui lòng thử lại");
            return;
        }
        if (typeof window.ClipboardItem === "undefined" || !navigator.clipboard?.write) {
            message.warning("Trình duyệt không hỗ trợ chép ảnh, vui lòng dùng nút Tải ảnh QR");
            return;
        }
        try {
            // Truyền thẳng Promise blob vào ClipboardItem, KHÔNG await trước khi gọi write:
            // Chrome đòi write() nằm trong cùng nhịp tương tác của người dùng, await xong mới
            // gọi là mất "user activation" và bị chặn.
            const blobPromise: Promise<Blob> = new Promise((resolve, reject) => {
                canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("no blob"))), "image/png");
            });
            // Nếu cửa sổ trình duyệt không giữ focus, Chrome để write() treo vô hạn (không
            // resolve cũng không reject) -> chặn thời gian chờ để người dùng luôn có phản hồi.
            await Promise.race([
                navigator.clipboard.write([new window.ClipboardItem({ "image/png": blobPromise })]),
                new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 4000)),
            ]);
            message.success("Đã sao chép ảnh QR, dán vào khung chat để gửi khách");
        } catch {
            message.warning("Không chép được ảnh, vui lòng dùng nút Tải ảnh QR");
        }
    };

    useEffect(() => {
        setRatings({ ...EMPTY_RATINGS, ...(survey?.ratings || {}) } as CareSurveyRatings);
        setCareNote(survey?.care_note || "");
        setEditing(false);
    }, [appointmentId, survey]);

    const filled = RATING_FIELDS.map((f) => Number(ratings[f.key]) || 0).filter((v) => v > 0);
    const avg = filled.length ? Math.round((filled.reduce((s, v) => s + v, 0) / filled.length) * 10) / 10 : 0;
    const readOnly = !!isCared && !editing;

    const handleSave = async () => {
        if (!appointmentId) return;
        const missingRating = RATING_FIELDS.some((f) => !ratings[f.key] || Number(ratings[f.key]) < 1);
        if (missingRating) {
            message.warning("Vui lòng đánh giá đủ 4 tiêu chí (1-5 sao)");
            return;
        }
        if (!careNote.trim()) {
            message.warning("Vui lòng nhập ghi chú chăm sóc");
            return;
        }
        setSaving(true);
        const res = await updateAppointmentCare(appointmentId, { care_note: careNote.trim(), ratings });
        setSaving(false);
        if (res.status) {
            message.success(res.msg || "Đã lưu khảo sát chăm sóc");
            setEditing(false);
            onUpdated?.();
        } else {
            message.error(res.msg || "Lưu khảo sát thất bại");
        }
    };

    return (

        <div className="h-[calc(100vh-245px)] overflow-y-auto p-5 bg-slate-50 dark:bg-[#1f1f1f]">

            {/* Link + QR để nhân viên gửi cho khách tự đánh giá */}
            {surveyUrl && (
                <Card className="mb-4">

                    <h2 className="font-semibold text-lg mb-1 text-000000e0">
                        Gửi khách tự đánh giá
                    </h2>

                    <p className="text-[13px] text-slate-500 mb-4">
                        Gửi link hoặc ảnh QR bên dưới cho khách. Mỗi lịch hẹn chỉ đánh giá được một lần.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4">

                        <div className="flex-1 min-w-0">

                            <Space.Compact className="w-full">
                                <Input readOnly value={surveyUrl} />
                                <Button icon={<CopyOutlined />} onClick={handleCopyLink}>
                                    Sao chép
                                </Button>
                            </Space.Compact>

                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button icon={<PictureOutlined />} onClick={handleCopyQR}>
                                    Sao chép ảnh QR
                                </Button>
                                <Button icon={<DownloadOutlined />} onClick={handleDownloadQR}>
                                    Tải ảnh QR
                                </Button>
                            </div>

                        </div>

                        <div ref={qrRef} className="shrink-0 self-center sm:self-start">
                            <QRCode value={surveyUrl} size={132} bordered={false} />
                        </div>

                    </div>

                </Card>
            )}

            <Card>

                <div className="flex justify-between items-center mb-6 text-000000e0">

                    <h2 className="font-semibold text-lg">
                        Phiếu khảo sát hài lòng
                    </h2>

                    <Tag
                        color={avg === 0 ? "default" : avg < 3 ? "error" : avg < 4 ? "warning" : "success"}
                        className="rounded-full px-3"
                    >
                        {avg ? `${avg} / 5.0 Sao` : "Chưa đánh giá"}
                    </Tag>

                </div>

                {isCared && (caredBy || caredAt) && (
                    <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-[13px] text-green-700">
                        Đã chăm sóc{caredBy ? ` bởi ${caredBy}` : ""}{caredAt ? ` lúc ${caredAt}` : ""}
                    </div>
                )}

                <div className="divide-y text-000000e0">

                    {RATING_FIELDS.map((item) => (

                        <div
                            key={item.key}
                            className="flex justify-between py-5"
                        >

                            <span>
                                {item.title}
                            </span>

                            <Rate
                                disabled={readOnly}
                                value={Number(ratings[item.key]) || 0}
                                onChange={(v) => setRatings((prev) => ({ ...prev, [item.key]: v }))}
                            />

                        </div>

                    ))}

                </div>

                {/* Chờ API — trường "giới thiệu bạn bè" backend chưa hỗ trợ */}
                <div className="mt-8 text-000000e0">

                    <div className="font-medium mb-3">
                        Khách có giới thiệu bạn bè không?
                    </div>

                    <Tag>
                        Không
                    </Tag>

                </div>

                <div className="mt-8 text-000000e0">

                    <div className="font-medium mb-3">
                        Ý kiến bổ sung của khách hàng:
                    </div>

                    {readOnly ? (
                        careNote ? (
                            <div
                                className={`rounded-xl border p-4 italic ${avg > 0 && avg < 3
                                    ? "border-red-200 bg-red-50 text-red-700"
                                    : "border-slate-200 bg-slate-50 text-slate-700"
                                    }`}
                            >
                                "{careNote}"
                            </div>
                        ) : (
                            <div className="text-slate-400">—</div>
                        )
                    ) : (
                        <TextArea
                            rows={4}
                            placeholder="Nhập nội dung ghi chú chăm sóc khách hàng..."
                            value={careNote}
                            onChange={(e) => setCareNote(e.target.value)}
                            maxLength={2000}
                            showCount
                        />
                    )}

                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {readOnly ? (
                        <Button onClick={() => setEditing(true)}>
                            Chỉnh sửa khảo sát
                        </Button>
                    ) : (
                        <>
                            {isCared && (
                                <Button
                                    onClick={() => {
                                        setRatings({ ...EMPTY_RATINGS, ...(survey?.ratings || {}) } as CareSurveyRatings);
                                        setCareNote(survey?.care_note || "");
                                        setEditing(false);
                                    }}
                                >
                                    Hủy
                                </Button>
                            )}
                            <Button
                                type="primary"
                                loading={saving}
                                onClick={handleSave}
                            >
                                {isCared ? "Cập nhật khảo sát" : "Lưu khảo sát"}
                            </Button>
                        </>
                    )}
                </div>

            </Card>

        </div>

    );

}
